import { FunctionComponent, useMemo } from "react";

import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Flex,
    Box,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Select,
    Textarea,
    SimpleGrid,
    Button,
    VStack,
    Spacer,
    Divider,
    Text,
    AbsoluteCenter,
} from "@chakra-ui/react";
import { UseQueryResult } from "@tanstack/react-query";
import { DatePicker, useToast, RepeatShimmer, dropTimeFromIsoFormat, useConfirm, FileContainer } from "am-tax-fe-core";
import { useForm, SubmitHandler } from "react-hook-form";
import { useParams } from "react-router-dom";
import useState from "react-usestateref";

import {
    Engagement,
    TaxNotice,
    Project,
    PostEngagementTaxNoticeArgs,
    PostProjectTaxNoticeArgs,
    UploadDocumentsToEngagementTaxNoticeArgs,
    UploadDocumentsToProjectTaxNoticeArgs,
    DeleteEngagementTaxNoticeArgs,
    DeleteProjectTaxNoticeArgs,
    PutEngagementTaxNoticeArgs,
    PutProjectTaxNoticeArgs,
    User,
    Participant,
    State,
    useStates,
    useTaxNoticeStatuses,
    useTaxTypes,
} from "../../api";
import { AreaPermissions, TaxNoticeArea } from "../../auth/Permissions.ts";
import DocumentListPanel from "../../components/DocumentListPanel";
import { Mutation } from "../../utils/query-utils.ts";

export type ProjectEngagement = Project | Engagement;
export type SaveTaxNoticeArgs = PostEngagementTaxNoticeArgs | PostProjectTaxNoticeArgs | PutEngagementTaxNoticeArgs | PutProjectTaxNoticeArgs;
export type DeleteTaxNoticeArgs = DeleteEngagementTaxNoticeArgs | DeleteProjectTaxNoticeArgs;
export type UploadDocumentsToTaxNoticeArgs = UploadDocumentsToEngagementTaxNoticeArgs | UploadDocumentsToProjectTaxNoticeArgs;

export interface TaxNoticeFormProps {
    projectEngagementQuery: UseQueryResult<ProjectEngagement>;
    taxNoticeQuery: UseQueryResult<TaxNotice>;
    saveTaxNoticeMutation: Mutation<SaveTaxNoticeArgs, TaxNotice>;
    deleteTaxNoticeMutation: Mutation<DeleteTaxNoticeArgs, number>;
    uploadFileToTaxNoticeMutation: Mutation<UploadDocumentsToTaxNoticeArgs>;
    participantsQuery: UseQueryResult<Participant[]>;
    navigateBack: () => void;
    onSaveComplete: (projectOrEngagement: ProjectEngagement, taxNotice: TaxNotice, filesUploaded?: boolean) => void;
    areaPermissions: AreaPermissions<typeof TaxNoticeArea>;
}

export const TaxNoticeForm: FunctionComponent<TaxNoticeFormProps> = ({
    projectEngagementQuery,
    taxNoticeQuery,
    saveTaxNoticeMutation,
    deleteTaxNoticeMutation,
    uploadFileToTaxNoticeMutation,
    participantsQuery,
    navigateBack,
    onSaveComplete,
    areaPermissions,
}) => {
    const toast = useToast();

    const { engagementId, projectId } = useParams();

    const [uploading, setUploading] = useState(false);
    const [fileQueue, setFileQueue] = useState<FileContainer[]>([]);
    const [, setNewNoticeJustSaved, newNoticeJustSavedRef] = useState<TaxNotice | undefined>(undefined);

    //Queries
    const statesQuery = useStates();
    const usStates = statesQuery?.data ?? new Array<State>();
    const taxNoticeStatusesQuery = useTaxNoticeStatuses();
    const taxNoticeStatuses = taxNoticeStatusesQuery.data;
    const taxTypesQuery = useTaxTypes();
    const taxTypes = taxTypesQuery.data;
    const taxNotice = taxNoticeQuery.data;
    const projectOrEngagement = projectEngagementQuery.data;

    const currentDate = new Date();
    const year = Number(currentDate.getFullYear());

    const users: User[] = useMemo(() => {
        const participantList = participantsQuery.data;
        const users = participantList?.map(p => p.user) || [];
        if (taxNotice?.assignedTo && !participantList?.find(p => p.user.id === taxNotice.assignedTo?.id)) {
            users.push(taxNotice.assignedTo);
        }
        return users;
    }, [taxNotice, participantsQuery.data]);

    const formValues: Partial<SaveTaxNoticeArgs> = {
        taxPayerId: taxNotice?.taxPayerId,
        taxYear: taxNotice?.taxYear,
        noticeId: taxNotice?.noticeId,
        noticeDate: dropTimeFromIsoFormat(taxNotice?.noticeDate) || "",
        notes: taxNotice?.notes,
        jurisdiction: taxNotice?.jurisdiction || "",
        status: taxNotice?.status?.id,
        taxType: taxNotice?.taxType?.id,
        balanceDue: taxNotice?.balanceDue,
        letterId: taxNotice?.letterId || "",
        entity: taxNotice?.entity || "",
        assignedTo: taxNotice?.assignedTo?.id,
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Partial<SaveTaxNoticeArgs>>({
        values: formValues,
    });

    const uploadFile = (fileContainer: FileContainer, taxNoticeId?: string | undefined) => {
        fileContainer.metaData = { folderId: projectOrEngagement?.clientDocumentsRootFolderId || "" };
        const args = uploadFileToTaxNoticeMutation.updateArgs({
            engagementId: engagementId ?? "",
            taxNoticeId: taxNoticeId || taxNotice?.id || "",
            fileContainer,
        });
        uploadFileToTaxNoticeMutation.query.mutate(args);
    };

    const onSubmit: SubmitHandler<Partial<SaveTaxNoticeArgs>> = async data => {
        const args = saveTaxNoticeMutation.updateArgs({
            taxNoticeId: taxNotice?.id,
            engagementId: engagementId || "",
            projectId: projectId || "",
            taxPayerId: data.taxPayerId,
            noticeId: data.noticeId,
            notes: data.notes,
            noticeDate: data.noticeDate || "",
            taxYear: data.taxYear,
            jurisdiction: data.jurisdiction,
            status: data.status,
            taxType: data.taxType,
            balanceDue: data.balanceDue,
            letterId: data.letterId,
            entity: data.entity,
            assignedTo: data.assignedTo,
        });

        const response = await saveTaxNoticeMutation.query.mutateAsync(args);
        if (!taxNotice) {
            setNewNoticeJustSaved(response);
        }
        if (fileQueue.length > 0) {
            setUploading(true);
            for (const fileContainer of fileQueue) {
                uploadFile(fileContainer, response.id);
            }
        } else {
            onSaveComplete(projectOrEngagement!, response as TaxNotice);
            toast({
                title: "Saved",
                description: "Tax Notice Saved.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            navigateBack();
        }
    };

    const { confirm, ConfirmDialog } = useConfirm({ title: "Delete the Tax Notice", prompt: "Are you sure you want to delete this tax notice?" });
    const doDelete = async () => {
        const result = await confirm();
        if (result) {
            const args = deleteTaxNoticeMutation.updateArgs({ taxNoticeId: taxNotice?.id || "", engagementId: engagementId ?? "" });
            deleteTaxNoticeMutation.query.mutate(args, {
                onSuccess: () => {
                    toast({
                        title: "Deleted",
                        description: "Tax Notice Deleted.",
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                    });
                    navigateBack();
                },
            });
        }
    };

    const onUploadComplete = async () => {
        try {
            const currentTaxNotice = taxNotice || newNoticeJustSavedRef.current;
            onSaveComplete(projectOrEngagement!, currentTaxNotice!, true);
            setFileQueue([]);
            toast({
                title: "Saved",
                description: "Saved Request and Uploaded Files.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            if (newNoticeJustSavedRef.current) {
                setNewNoticeJustSaved(undefined);
                navigateBack();
            }
        } finally {
            setUploading(false);
        }
    };

    if (taxNoticeQuery.isLoading) {
        return (
            <VStack spacing="1px" alignItems="stretch">
                <RepeatShimmer times={5} height="40px" />
            </VStack>
        );
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                    <FormControl isInvalid={!!errors?.noticeId}>
                        <FormLabel className="required">Notice Title</FormLabel>
                        <Input {...register("noticeId", { required: "Notice Title is required." })} />
                        <FormErrorMessage>{errors?.noticeId?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors?.noticeDate}>
                        <FormLabel className="required">Notice Date</FormLabel>
                        <DatePicker {...register("noticeDate", { required: "Notice Date is required." })} />
                        <FormErrorMessage>{errors?.noticeDate?.message}</FormErrorMessage>
                    </FormControl>
                </SimpleGrid>

                <Box my="1rem">
                    <FormControl>
                        <FormLabel>Notes</FormLabel>
                        <Textarea
                            {...register("notes")}
                            maxLength={4000} //is there a way to import the column definition constants here?
                        />
                    </FormControl>
                </Box>

                <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                    <FormControl>
                        <FormLabel>Taxpayer ID</FormLabel>
                        {/*<MaskedSsnInput {...register("taxPayerId")} />*/}
                        <Input {...register("taxPayerId")} />
                    </FormControl>
                    <FormControl isInvalid={!!errors?.taxYear}>
                        <FormLabel>Tax Year</FormLabel>
                        {/*can set max to be year*/}
                        <NumberInput defaultValue={year} max={year} min={0}>
                            <NumberInputField {...register("taxYear", { required: "Tax Year is required." })} />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                        <FormErrorMessage>{errors?.taxYear?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors?.jurisdiction}>
                        <FormLabel>State/Federal Governing Body</FormLabel>
                        <Select placeholder="Select an option..." {...register("jurisdiction", { required: "State/Federal Governing Body is required." })}>
                            {usStates.map(state => (
                                <option key={state.code} value={state.name}>
                                    {state.name}
                                </option>
                            ))}
                        </Select>
                        <FormErrorMessage>{errors?.jurisdiction?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors?.status}>
                        <FormLabel>Status</FormLabel>
                        <Select placeholder="Select a status..." {...register("status", { required: "Status is required." })}>
                            {taxNoticeStatuses?.map(status => (
                                <option key={status.id} value={status.id}>
                                    {status.name}
                                </option>
                            ))}
                        </Select>
                        <FormErrorMessage>{errors?.status?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors?.taxType}>
                        <FormLabel>Tax Type</FormLabel>
                        <Select placeholder="Select a tax type..." {...register("taxType", { required: "Tax Type is required." })}>
                            {taxTypes?.map(taxType => (
                                <option key={taxType.id} value={taxType.id}>
                                    {taxType.name}
                                </option>
                            ))}
                        </Select>
                        <FormErrorMessage>{errors?.taxType?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors?.balanceDue}>
                        <FormLabel>Balance Due</FormLabel>
                        <Input {...register("balanceDue")} />
                        <FormErrorMessage>{errors?.balanceDue?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors?.letterId}>
                        <FormLabel>Letter ID</FormLabel>
                        <Input {...register("letterId")} />
                        <FormErrorMessage>{errors?.letterId?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors?.entity}>
                        <FormLabel>Entity ID</FormLabel>
                        <Input {...register("entity")} />
                        <FormErrorMessage>{errors?.entity?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors?.assignedTo}>
                        <FormLabel>Assigned To</FormLabel>
                        <Select placeholder="Select a assignee..." {...register("assignedTo")}>
                            {users?.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.firstName} {user.lastName}
                                </option>
                            ))}
                        </Select>

                        <FormErrorMessage>{errors?.assignedTo?.message}</FormErrorMessage>
                    </FormControl>
                </SimpleGrid>

                {areaPermissions.hasAny(TaxNoticeArea.update, TaxNoticeArea.delete, TaxNoticeArea.create) && (
                    <Flex justifyContent="flex-end" mt={"2rem"} width={"full"}>
                        {taxNotice && (
                            <>
                                <Button
                                    variant="ghost"
                                    onClick={doDelete}
                                    colorScheme={"red"}
                                    fontWeight={200}
                                    isDisabled={uploading || saveTaxNoticeMutation.query.isPending}
                                    isLoading={deleteTaxNoticeMutation.query.isPending}
                                    loadingText="Deleting"
                                >
                                    Delete Notice
                                </Button>
                                <Spacer />
                            </>
                        )}

                        <Button type="submit" variant={"primary"} mr={3} isLoading={saveTaxNoticeMutation.query.isPending} loadingText="Saving">
                            Save
                        </Button>
                        <Button variant="ghost" onClick={navigateBack}>
                            Cancel
                        </Button>
                    </Flex>
                )}
            </form>

            <Box sx={{ position: "relative", py: 10 }}>
                <Divider />
                <AbsoluteCenter bg="white" px="4">
                    <Text fontSize="sm">Tax Notice Files</Text>
                </AbsoluteCenter>
            </Box>

            <DocumentListPanel
                engagementId={engagementId ?? ""}
                documents={taxNotice?.documents}
                projectId={projectId}
                immediateUpload={!!taxNotice?.id}
                fileQueue={fileQueue}
                setFileQueue={setFileQueue}
                uploadFile={uploadFile}
                onUploadComplete={onUploadComplete}
                canUpload={true}
            />

            <ConfirmDialog />
        </>
    );
};
